/**
 * Minimal Lexical → React renderer. Handles the node types Payload's
 * lexicalEditor() produces with no extra features (paragraph, heading,
 * text, linebreak, list). Renders into `.rich-text` so the
 * prose styles apply.
 *
 * Why no @payloadcms/richtext-lexical/html: that package pulls in Node-only
 * deps. We only need the read path on the client, so walking the JSON tree
 * is simpler and ~50 lines.
 */
import { Fragment } from 'react'

type LexNode = {
  type: string
  tag?: string
  format?: number | string
  text?: string
  url?: string
  children?: LexNode[]
  listType?: 'bullet' | 'number'
  version?: number
}

const FORMAT_BOLD = 1
const FORMAT_ITALIC = 1 << 1
const FORMAT_STRIKETHROUGH = 1 << 2
const FORMAT_UNDERLINE = 1 << 3
const FORMAT_CODE = 1 << 4

function renderText(node: LexNode, key: number) {
  if (typeof node.text !== 'string') return null
  let el: React.ReactNode = node.text
  const f = typeof node.format === 'number' ? node.format : 0
  if (f & FORMAT_CODE)          el = <code key={key}>{el}</code>
  if (f & FORMAT_STRIKETHROUGH) el = <s key={key}>{el}</s>
  if (f & FORMAT_UNDERLINE)     el = <u key={key}>{el}</u>
  if (f & FORMAT_ITALIC)        el = <em key={key}>{el}</em>
  if (f & FORMAT_BOLD)          el = <strong key={key}>{el}</strong>
  return <Fragment key={key}>{el}</Fragment>
}

function renderNode(node: LexNode, key: number): React.ReactNode {
  const children = (node.children ?? []).map((c, i) => renderNode(c, i))

  switch (node.type) {
    case 'text':       return renderText(node, key)
    case 'linebreak':  return <br key={key} />
    case 'paragraph':  return <p key={key}>{children}</p>
    case 'heading': {
      const Tag = (node.tag ?? 'h3') as keyof React.JSX.IntrinsicElements
      return <Tag key={key}>{children}</Tag>
    }
    case 'list': {
      const ListTag = node.listType === 'number' ? 'ol' : 'ul'
      return <ListTag key={key}>{children}</ListTag>
    }
    case 'listitem':   return <li key={key}>{children}</li>
    case 'quote':      return <blockquote key={key}>{children}</blockquote>
    case 'link': {
      const url = node.url ?? '#'
      const external = /^https?:\/\//.test(url)
      return (
        <a key={key} href={url} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
          {children}
        </a>
      )
    }
    default: return <Fragment key={key}>{children}</Fragment>
  }
}

type LexicalRoot = { root?: { children?: LexNode[] } }

export function LexicalRender({ data }: { data: unknown }) {
  const root = (data as LexicalRoot | undefined)?.root
  if (!root?.children?.length) return null
  return (
    <div className="rich-text">
      {root.children.map((n, i) => renderNode(n, i))}
    </div>
  )
}
